import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, Button, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, IconButton, Chip,
  TextField, Snackbar, Alert, CircularProgress, Tooltip,
  Select, MenuItem, FormControl, InputLabel, TableSortLabel
} from '@mui/material';
import { Label, Add, Edit, Delete, Refresh } from '@mui/icons-material';
import axios from 'axios';
import { extractArrayFromResponse } from '../utils/apiHelpers';
import { ColumnToggle } from './common/ColumnToggle';
import { Modal } from './common/Modal';

interface Alias {
  id: number;
  tour_id: number;
  alias: string;
  alias_name?: string;
  tour_name?: string;
  created_at: string;
}

interface Tour {
  id: number;
  name: string;
}

type Order = 'asc' | 'desc';
type SortableKey = 'id' | 'alias' | 'tour_name';

const AliasManager: React.FC = () => {
  const [aliases, setAliases] = useState<Alias[]>([]);
  const [tours, setTours] = useState<Tour[]>([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingAlias, setEditingAlias] = useState<Alias | null>(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [formData, setFormData] = useState({ tour_id: 0, alias: '' });

  const [order, setOrder] = useState<Order>('asc');
  const [orderBy, setOrderBy] = useState<SortableKey>('id');

  const columns = [
    { key: 'id', label: 'ID', defaultVisible: true },
    { key: 'alias', label: 'Alias', defaultVisible: true },
    { key: 'tour_name', label: 'Программа', defaultVisible: true },
  ];

  const [visibleColumns, setVisibleColumns] = useState<string[]>([]);

  useEffect(() => {
    const saved = localStorage.getItem('aliases_visible_columns');
    if (saved) {
      try {
        setVisibleColumns(JSON.parse(saved));
        return;
      } catch (e) {}
    }
    setVisibleColumns(columns.filter(c => c.defaultVisible).map(c => c.key));
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [aliasesRes, toursRes] = await Promise.all([
        axios.get('http://192.168.50.43:5002/api/aliases'),
        axios.get('http://192.168.50.43:5002/api/tours')
      ]);
      
      const rawAliases = extractArrayFromResponse(aliasesRes.data);
      const normalizedAliases = rawAliases.map((item: any) => ({
        id: item.id,
        tour_id: item.tour_id,
        alias: item.alias_name || item.alias || '',
        tour_name: item.tour_name || '',
        created_at: item.created_at,
      }));
      
      setAliases(normalizedAliases);
      setTours(extractArrayFromResponse(toursRes.data));
    } catch (error) {
      console.error('Ошибка загрузки:', error);
      setSnackbar({ open: true, message: 'Ошибка загрузки данных', severity: 'error' });
      setAliases([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleSave = async () => {
    if (!formData.alias || !formData.tour_id) {
      setSnackbar({ open: true, message: 'Заполните все поля', severity: 'error' });
      return;
    }
    try {
      if (editingAlias) {
        await axios.put(`http://192.168.50.43:5002/api/aliases/${editingAlias.id}`, formData);
        setSnackbar({ open: true, message: 'Алиас обновлен', severity: 'success' });
      } else {
        await axios.post('http://192.168.50.43:5002/api/aliases', formData);
        setSnackbar({ open: true, message: 'Алиас создан', severity: 'success' });
      }
      setOpenDialog(false);
      loadData();
    } catch (error) {
      console.error('Ошибка сохранения:', error);
      setSnackbar({ open: true, message: 'Ошибка сохранения', severity: 'error' });
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Удалить алиас?')) return;
    try {
      await axios.delete(`http://192.168.50.43:5002/api/aliases/${id}`);
      setSnackbar({ open: true, message: 'Алиас удален', severity: 'success' });
      loadData();
    } catch (error) {
      console.error('Ошибка удаления:', error);
      setSnackbar({ open: true, message: 'Ошибка удаления', severity: 'error' });
    }
  };

  const handleRequestSort = (property: SortableKey) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const sortedAliases = [...aliases].sort((a, b) => {
    let aValue: any = a[orderBy as keyof Alias] || '';
    let bValue: any = b[orderBy as keyof Alias] || '';
    
    if (orderBy === 'tour_name') {
      const tourA = tours.find(t => t.id === a.tour_id);
      const tourB = tours.find(t => t.id === b.tour_id);
      aValue = tourA?.name || '';
      bValue = tourB?.name || '';
    }
    
    if (typeof aValue === 'string') {
      aValue = aValue.toLowerCase();
      bValue = bValue.toLowerCase();
    }
    
    if (aValue < bValue) return order === 'asc' ? -1 : 1;
    if (aValue > bValue) return order === 'asc' ? 1 : -1;
    return 0;
  });

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  const isVisible = (key: string) => visibleColumns.includes(key);

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Label sx={{ fontSize: 32, color: 'primary.main' }} />
          
          <Chip label={`${aliases.length} алиасов`} size="small" variant="outlined" />
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" startIcon={<Refresh />} onClick={loadData} size="small">Обновить</Button>
          <Button variant="contained" startIcon={<Add />} onClick={() => { setEditingAlias(null); setFormData({ tour_id: 0, alias: '' }); setOpenDialog(true); }}>Добавить</Button>
        </Box>
      </Box>

      <Paper sx={{ p: 2, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
          <ColumnToggle
            columns={columns}
            storageKey="aliases_visible_columns"
            onChange={(cols) => setVisibleColumns(cols)}
          />
        </Box>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                {isVisible('id') && (
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'id'}
                      direction={orderBy === 'id' ? order : 'asc'}
                      onClick={() => handleRequestSort('id')}
                      sx={{ fontWeight: 'bold' }}
                    >
                      ID
                    </TableSortLabel>
                  </TableCell>
                )}
                {isVisible('alias') && (
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'alias'}
                      direction={orderBy === 'alias' ? order : 'asc'}
                      onClick={() => handleRequestSort('alias')}
                      sx={{ fontWeight: 'bold' }}
                    >
                      Alias
                    </TableSortLabel>
                  </TableCell>
                )}
                {isVisible('tour_name') && (
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'tour_name'}
                      direction={orderBy === 'tour_name' ? order : 'asc'}
                      onClick={() => handleRequestSort('tour_name')}
                      sx={{ fontWeight: 'bold' }}
                    >
                      Программа
                    </TableSortLabel>
                  </TableCell>
                )}
                <TableCell align="right">Действия</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedAliases.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    <Typography color="text.secondary" sx={{ py: 2 }}>Нет алиасов</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                sortedAliases.map((alias) => {
                  const tourName = tours.find(t => t.id === alias.tour_id)?.name || alias.tour_name || 'Неизвестно';
                  const isActive = true;
                  return (
                    <TableRow 
                      key={alias.id} 
                      hover
                      sx={{ 
                        opacity: isActive ? 1 : 0.6,
                        bgcolor: isActive ? 'inherit' : '#f5f5f5',
                        '&:hover': {
                          bgcolor: isActive ? 'action.hover' : '#e8e8e8',
                        }
                      }}
                    >
                      {isVisible('id') && <TableCell>{alias.id}</TableCell>}
                      {isVisible('alias') && <TableCell>{alias.alias || '-'}</TableCell>}
                      {isVisible('tour_name') && <TableCell>{tourName}</TableCell>}
                      <TableCell align="right">
                        <Tooltip title="Редактировать">
                          <IconButton 
                            size="small" 
                            color="primary" 
                            onClick={() => { 
                              setEditingAlias(alias); 
                              setFormData({ tour_id: alias.tour_id, alias: alias.alias }); 
                              setOpenDialog(true); 
                            }}
                          >
                            <Edit fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Удалить">
                          <IconButton size="small" color="error" onClick={() => handleDelete(alias.id)}>
                            <Delete fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Modal
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        title={editingAlias ? 'Редактирование алиаса' : 'Новый алиас'}
        actions={
          <>
            <Button 
              onClick={() => setOpenDialog(false)} 
              variant="outlined"
              sx={{ 
                borderRadius: 2,
                px: 3,
                py: 1,
                textTransform: 'none',
                fontWeight: 500,
              }}
            >
              Отмена
            </Button>
            <Button 
              onClick={handleSave} 
              variant="contained"
              sx={{ 
                borderRadius: 2,
                px: 3,
                py: 1,
                textTransform: 'none',
                fontWeight: 500,
              }}
            >
              {editingAlias ? 'Обновить' : 'Создать'}
            </Button>
          </>
        }
      >
        <Box sx={{ pt: 3, display: 'flex', flexDirection: 'column', gap: 3 }}>
          <FormControl fullWidth>
            <InputLabel>Программа</InputLabel>
            <Select
              value={formData.tour_id}
              onChange={(e) => setFormData({ ...formData, tour_id: Number(e.target.value) })}
              label="Программа"
              sx={{
                borderRadius: 2,
                bgcolor: '#ffffff',
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                }
              }}
            >
              <MenuItem value={0}>Выберите программу</MenuItem>
              {tours.map((t) => (
                <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField 
            label="Alias" 
            value={formData.alias} 
            onChange={(e) => setFormData({ ...formData, alias: e.target.value })} 
            fullWidth 
            required 
            variant="outlined"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                bgcolor: '#ffffff',
              }
            }}
          />
        </Box>
      </Modal>

      <Snackbar open={snackbar.open} autoHideDuration={5000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}>
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default AliasManager;
